import { Outlet, createBrowserRouter } from 'react-router';

import {
  AnonymousGuard,
  AuthGate,
  RequireAuth,
  RequireRole,
  RootRedirect,
  hasNotAllowedRole,
  isManager,
  isNewUser,
  isStoreManager,
  LoginPage,
} from '~domains/auth';
import { PartnerApplicationCreatePage } from '~domains/partner-application/pages/partner-application-create';
import { PartnerApplicationEditPage } from '~domains/partner-application/pages/partner-application-edit';
import { PartnerApplicationMenuPage } from '~domains/partner-application/pages/partner-application-menu';
import { PartnerApplicationSignPage } from '~domains/partner-application/pages/partner-application-sign';
import { ProfilePage } from '~domains/profile/pages/profile';

import { AccessDeniedPage } from '~pages/access-denied';
import { DashboardPage } from '~pages/dashboard';
import { LoansPage } from '~pages/loans';
import { CreateOrderPage, OrdersPage } from '~pages/orders';
import { PaymentsPage } from '~pages/payments';
import { TransactionsPage } from '~pages/transactions';

import { MobilePagesLayout, PagesLayout } from '~shared/ui/layout';

import * as Sentry from '@breeze/web-lib-sentry';

import { PartnerRedirect } from './partner-redirect';
import { ProfileStateGuard } from './profile-state-guard';

const createBrowserRouterWithSentry =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = createBrowserRouterWithSentry([
  {
    element: <AuthGate />,
    children: [
      {
        path: '*',
        element: <RootRedirect />,
      },
      {
        path: 'login',
        element: (
          <AnonymousGuard>
            <MobilePagesLayout>
              <LoginPage />
            </MobilePagesLayout>
          </AnonymousGuard>
        ),
      },
      {
        element: (
          <RequireAuth>
            <Outlet />
          </RequireAuth>
        ),
        children: [
          {
            path: 'access-denied',
            element: (
              <RequireRole roles={[hasNotAllowedRole]}>
                <AccessDeniedPage />
              </RequireRole>
            ),
          },
          {
            path: 'partner',
            element: (
              <RequireRole roles={[isNewUser]}>
                <MobilePagesLayout>
                  <Outlet />
                </MobilePagesLayout>
              </RequireRole>
            ),
            children: [
              {
                index: true,
                element: <PartnerRedirect />,
              },
              {
                path: 'profile',
                element: (
                  <ProfileStateGuard shouldBeFilled={false}>
                    <ProfilePage />
                  </ProfileStateGuard>
                ),
              },
              {
                element: (
                  <ProfileStateGuard shouldBeFilled={true}>
                    <Outlet />
                  </ProfileStateGuard>
                ),
                children: [
                  {
                    path: 'application',
                    element: <PartnerApplicationCreatePage />,
                  },
                  {
                    path: 'application/menu',
                    element: <PartnerApplicationMenuPage />,
                  },
                  {
                    path: 'application/:id',
                    element: <PartnerApplicationEditPage />,
                  },
                  {
                    path: 'application/:id/sign',
                    element: <PartnerApplicationSignPage />,
                  },
                ],
              },
            ],
          },
          {
            element: (
              <RequireRole roles={[isStoreManager, isManager]}>
                <PagesLayout />
              </RequireRole>
            ),
            children: [
              {
                path: 'dashboard',
                element: <DashboardPage />,
              },
              {
                path: 'transactions',
                element: <TransactionsPage />,
                children: [
                  {
                    path: 'loans',
                    element: <LoansPage />,
                  },
                  {
                    path: 'payments',
                    element: <PaymentsPage />,
                  },
                ],
              },
              {
                path: 'orders',
                children: [
                  {
                    index: true,
                    element: <OrdersPage />,
                  },
                  {
                    path: 'create',
                    element: <CreateOrderPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
